/**
 * Seed Selection State Management Hook
 *
 * Manages row selection state for the seeds table, including:
 * - Selected seed keys (key-based selection for reliability)
 * - Computed selected seeds from keys
 * - Helper methods for selection operations
 * - Automatic clearing when filters change
 *
 * Pattern: Consistent with useAssetSelection and useTechnologySelection
 * Risk Level: Low
 * - Has one useEffect for clearing selection on filter change
 * - Uses proper React dependencies for optimal performance
 * - Key-based selection survives pagination, sorting, and filtering
 * - Gracefully handles filtered or deleted seeds
 */

import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { SeedFilter } from '@/hooks/useSeeds';
import type { Seed } from '@/types';

/**
 * Return type for useSeedSelection hook
 */
interface SeedSelectionReturn {
  /** Array of selected seed keys */
  selectedKeys: string[];
  /** Direct setter for selected keys array (for multi-select support) */
  setSelectedKeys: (keys: string[]) => void;
  /** Boolean indicating if any rows are selected */
  hasSelection: boolean;
  /** Array of actual seed objects that are selected */
  selectedSeeds: Seed[];
  /** Function to toggle selection of a seed by key */
  toggleSelection: (key: string) => void;
  /** Function to clear all selections */
  clearSelection: () => void;
  /** Function to select all seeds */
  selectAll: () => void;
}

/**
 * Hook to manage row selection state for seeds table
 *
 * Uses key-based selection for reliability across pagination, filtering, and sorting.
 * Provides helper methods for common selection operations.
 *
 * Uses proper React dependency tracking for optimal performance:
 * - useMemo with direct dependencies (no JSON.stringify overhead)
 * - useRef + deep comparison for complex filter object changes
 *
 * @param seeds - Array of all seeds in the table
 * @param filter - Current filter state (used to clear selection on change)
 * @returns Object containing selection state and computed values
 *
 * @example
 * ```tsx
 * const { selectedKeys, setSelectedKeys, selectedSeeds, toggleSelection, clearSelection } = useSeedSelection(seeds, filter);
 *
 * // Use in table selection
 * <Table
 *   selection={{ value: selectedKeys, onChange: setSelectedKeys }}
 * />
 *
 * // Use selected seeds for actions
 * <Actions data={selectedSeeds} />
 *
 * // Toggle individual seed
 * toggleSelection(seed.key);
 *
 * // Clear all selections
 * clearSelection();
 * ```
 */
export const useSeedSelection = (
  seeds: Seed[],
  filter: SeedFilter
): SeedSelectionReturn => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const filterRef = useRef(filter);

  // Compute selected seeds from keys
  // Uses key-based lookup for reliability across pagination and filtering
  const { hasSelection, selectedSeeds } = useMemo(() => {
    const computedSelectedSeeds = selectedKeys
      .map(key => {
        const seed = seeds.find(s => s.key === key);

        if (!seed) {
          // Seed no longer in current view (filtered out, deleted, etc.)
          // This is expected behavior and not an error
          return null;
        }

        return seed;
      })
      .filter((seed): seed is Seed => seed !== null);

    return {
      hasSelection: selectedKeys.length > 0,
      selectedSeeds: computedSelectedSeeds,
    };
  }, [selectedKeys, seeds]);

  // Clear selection when filters change
  useEffect(() => {
    if (!isEqual(filterRef.current, filter)) {
      setSelectedKeys([]);
      filterRef.current = filter;
    }
  }, [filter]);

  // Toggle selection for a single seed
  const toggleSelection = (key: string) => {
    setSelectedKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Helper: Clear all selected rows
  const clearSelection = () => {
    setSelectedKeys([]);
  };

  // Helper: Select all seeds
  const selectAll = () => {
    setSelectedKeys(seeds.map(seed => seed.key));
  };

  return {
    selectedKeys,
    setSelectedKeys,
    hasSelection,
    selectedSeeds,
    toggleSelection,
    clearSelection,
    selectAll,
  };
};
